import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  KeyRound, LayoutDashboard, Search, Settings2, ShieldCheck, Users,
} from "lucide-react";

import { api } from "@/core/api/client";
import { useT } from "@/core/i18n";
import { useSession } from "@/core/state/session";
import { canReadIntegrationDocs } from "@/features/docs/visibility";

type CommandGroup = "nav" | "manage" | "platform" | "tenants";

type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  group: CommandGroup;
  path: string;
  keywords?: string;
};

/**
 * Lightweight command palette (Ctrl/Cmd+K): navigate pages and jump to tenants.
 * Keeps the control-plane scannable without adding another settings page.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const identity = useSession((state) => state.identity);
  const setManagementTenant = useSession((state) => state.setManagementTenant);
  const isAdmin = Boolean(identity?.roles.includes("tenant_admin") || identity?.roles.includes("super_admin"));
  const isSuper = Boolean(identity?.roles.includes("super_admin"));
  const canReadDocs = canReadIntegrationDocs(identity);
  const [query, setQuery] = useState("");

  const tenants = useQuery({
    queryKey: ["tenants", "command-palette"],
    queryFn: () => api.tenants() as Promise<{ items: Array<Record<string, unknown>> }>,
    enabled: open && isSuper,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const items = useMemo(() => {
    const base: CommandItem[] = [
      { id: "home", label: t("palette.home"), group: "nav", path: "/", keywords: "dashboard home 工作台总览" },
      { id: "account", label: t("palette.account"), group: "nav", path: "/account", keywords: "profile email 账号资料" },
      { id: "apps", label: t("palette.apps"), group: "nav", path: "/apps", keywords: "oauth clients 应用与授权" },
      { id: "security", label: t("palette.security"), group: "nav", path: "/security", keywords: "mfa session login 安全中心" },
      { id: "settings", label: t("palette.settings"), group: "nav", path: "/settings", keywords: "theme locale 系统设置" },
      ...(canReadDocs ? [{ id: "docs", label: t("palette.docs"), group: "nav" as const, path: "/docs", keywords: "api sdk oidc 对接文档" }] : []),
      { id: "support", label: t("support.title"), group: "nav", path: "/support", keywords: "ticket 工单" },
      { id: "announcements", label: t("announcements.title"), group: "nav", path: "/announcements", keywords: "notice 公告" },
    ];
    if (isAdmin) {
      base.push(
        { id: "admin", label: t("palette.admin"), group: "manage", path: "/admin", keywords: "users roles audit 租户管理" },
        { id: "admin-users", label: t("palette.users"), group: "manage", path: "/admin", keywords: "users invite 成员目录", hint: t("palette.usersHint") },
        { id: "admin-oidc", label: t("palette.oidc"), group: "manage", path: "/admin", keywords: "client oauth par" },
        { id: "admin-audit", label: t("palette.audit"), group: "manage", path: "/admin", keywords: "audit log 审计信号带" },
      );
    }
    if (isSuper) {
      base.push(
        { id: "platform", label: t("palette.platform"), group: "platform", path: "/platform", keywords: "tenant runtime 平台控制台" },
        { id: "system-group", label: t("palette.systemGroup"), group: "platform", path: "/platform?tab=super-admins", keywords: "super administrator 系统管理 权限组", hint: t("palette.systemGroupHint") },
        { id: "compliance", label: t("palette.compliance"), group: "platform", path: "/platform?tab=compliance", keywords: "archive session region 合规态势", hint: t("palette.complianceHint") },
        { id: "runtime", label: t("palette.runtime"), group: "platform", path: "/platform?tab=runtime", keywords: "archive retention worm 运行配置", hint: t("palette.runtimeHint") },
        { id: "mail-templates", label: t("palette.mail"), group: "platform", path: "/platform?tab=mail", keywords: "smtp email template 发信模板", hint: t("palette.mailHint") },
        { id: "ops", label: t("palette.ops"), group: "platform", path: "/platform?tab=ops", keywords: "queue audit log smtp 队列 日志", hint: t("palette.opsHint") },
        { id: "maintenance", label: t("maintenance.title"), group: "platform", path: "/platform?tab=maintenance", keywords: "maintenance 维护" },
      );
      for (const tenant of tenants.data?.items ?? []) {
        if (!tenant.is_active) continue;
        const slug = String(tenant.slug);
        base.push({
          id: `tenant-${slug}`,
          label: String(tenant.display_name ?? tenant.name ?? slug),
          hint: t("palette.workspace", { slug }),
          group: "tenants",
          path: "/admin",
          keywords: `${slug} tenant workspace 租户`,
        });
      }
    }
    const needle = query.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((item) =>
      [item.label, item.hint, item.keywords, item.group].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [canReadDocs, isAdmin, isSuper, query, t, tenants.data?.items]);

  function run(item: CommandItem) {
    if (item.id.startsWith("tenant-")) {
      const slug = item.id.replace("tenant-", "");
      setManagementTenant(slug);
      navigate("/admin");
    } else {
      navigate(item.path);
    }
    onClose();
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      destroyOnHidden
      className="command-palette-modal"
      styles={{ body: { paddingTop: 12 } }}
    >
      <Input
        autoFocus
        size="large"
        prefix={<Search size={16} />}
        placeholder={t("palette.placeholder")}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && items[0]) {
            event.preventDefault();
            run(items[0]);
          }
        }}
        aria-label={t("palette.search")}
      />
      <div className="command-palette-list" role="listbox">
        {items.length === 0 ? (
          <p className="command-palette-empty">{t("palette.empty")}</p>
        ) : (
          items.slice(0, 12).map((item) => (
            <button
              key={item.id}
              type="button"
              className="command-palette-item"
              onClick={() => run(item)}
            >
              <span className="command-palette-icon">{iconFor(item)}</span>
              <span className="command-palette-copy">
                <b>{item.label}</b>
                <small>{item.hint ?? groupLabel(item.group, t)}</small>
              </span>
              <kbd>{groupLabel(item.group, t)}</kbd>
            </button>
          ))
        )}
      </div>
      <p className="command-palette-hint">{t("palette.hint")}</p>
    </Modal>
  );
}

function groupLabel(group: CommandGroup, t: ReturnType<typeof useT>) {
  if (group === "nav") return t("palette.nav");
  if (group === "manage") return t("palette.manage");
  if (group === "platform") return t("palette.platformGroup");
  return t("palette.tenants");
}

function iconFor(item: CommandItem) {
  if (item.group === "tenants") return <Users size={16} />;
  if (item.path === "/security") return <ShieldCheck size={16} />;
  if (item.path === "/settings" || item.id === "runtime") return <Settings2 size={16} />;
  if (item.id.includes("oidc") || item.id.includes("audit")) return <KeyRound size={16} />;
  return <LayoutDashboard size={16} />;
}
